import React, { useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Menu,
  MenuItem,
  Box,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RepeatIcon from "@material-ui/icons/Repeat";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import StopIcon from "@material-ui/icons/Stop";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import WarningIcon from "@material-ui/icons/Warning";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import GuideOpenButton from "../../components/GuideOpenButton";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import AttachmentPreview from "../../components/AttachmentPreview";
import ConfirmationModal from "../../components/ConfirmationModal";
import GroupCampaignModal from "../../components/GroupCampaignModal";
import GroupCampaignExecutionsModal from "../../components/GroupCampaignExecutionsModal";
import toastError from "../../errors/toastError";
import { takePublicidadDraft } from "../Negocio/publicidadDraft";
import WhatsAppPolicyWarning from "../../components/WhatsAppPolicyWarning";
import CampaignsBrowseBar from "../../components/CampaignsBrowseBar";
import CampaignCard from "../../components/CampaignCard";
import ChannelConnectionLabel, { getCampaignChannel } from "../../components/ChannelConnectionLabel";
import { useDate } from "../../hooks/useDate";
import { useTimezone } from "../../hooks/useTimezone";
import api from "../../services/api";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    return Array.isArray(campaigns) ? campaigns : [];
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;
    return state.filter((c) => c.id !== campaignId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  // ✅ CORREGIDO: MainPaper dinámico según tema
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    borderRadius: "12px",
    boxShadow: theme.mode === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  // ✅ CORREGIDO: TableHeader dinámico según tema
  tableHeader: {
    backgroundColor: theme.mode === "dark" ? "#1f2937" : "#f8fafc",
    color: theme.mode === "dark" ? "#e5e7eb" : "#334155",
    fontWeight: 700,
    padding: theme.spacing(1.5),
  },
  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "#374151" : theme.palette.action.hover,
    },
    "& td": {
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.mode === "dark" ? "#4B5563" : "rgba(224, 224, 224, 0.5)"}`,
    },
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
  actionIcon: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.1)",
    },
  },
  deleteIcon: {
    color: "#d32f2f",
    "&:hover": {
      backgroundColor: "rgba(211, 47, 47, 0.1)",
    },
  },
  pauseIcon: {
    color: "#ff9800",
    "&:hover": {
      backgroundColor: "rgba(255, 152, 0, 0.1)",
    },
  },
  stopIcon: {
    color: "#d32f2f",
    "&:hover": {
      backgroundColor: "rgba(211, 47, 47, 0.1)",
    },
  },
  playIcon: {
    color: "#4caf50",
    "&:hover": {
      backgroundColor: "rgba(76, 175, 80, 0.1)",
    },
  },
  statusChip: {
    fontWeight: "bold",
    borderRadius: 16,
    padding: theme.spacing(0.5, 1),
  },
  statusScheduled: {
    backgroundColor: "#2196f3",
    color: "white",
  },
  statusProcessing: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  statusCompleted: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  statusFailed: {
    backgroundColor: "#f44336",
    color: "white",
  },
  statusCancelled: {
    backgroundColor: "#9e9e9e",
    color: "white",
  },
  // ✅ CORREGIDO: SearchField dinámico según tema
  gridWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: 16,
    padding: theme.spacing(1),
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.mode === "dark" ? "#374151" : "#f5f5f5",
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.mode === "dark" ? "#4B5563" : "#eeeeee",
      },
      "&.Mui-focused": {
        backgroundColor: theme.mode === "dark" ? "#374151" : "white",
        boxShadow: `0 0 0 2px ${theme.mode === "dark" ? "rgba(59, 130, 246, 0.3)" : "rgba(25, 118, 210, 0.2)"}`,
      },
      "& fieldset": {
        borderColor: theme.mode === "dark" ? "#4B5563" : "#e0e0e0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
}));

const GroupCampaigns = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { datetimeToClient } = useDate();
  const { formatDateTime } = useTimezone();
  const [loading, setLoading] = useState(true);
  const [campaigns, dispatch] = useReducer(reducer, []);
  const [searchParam, setSearchParam] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [publicidadPrefill, setPublicidadPrefill] = useState(null);
  const [publicidadAttachment, setPublicidadAttachment] = useState(null);
  const [selectedCampaignType, setSelectedCampaignType] = useState(null); // 'UNICA' o 'RECURRENTE'
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [campaignToPause, setCampaignToPause] = useState(null);
  const [campaignToStop, setCampaignToStop] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(13); // ✅ Paginación iniciando en 13 (igual que Campaigns)
  const [executionsModalOpen, setExecutionsModalOpen] = useState(false);
  const [botDetail, setBotDetail] = useState(null);
  const [selectedCampaignForExecutions, setSelectedCampaignForExecutions] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchCampaigns();

    // ✅ Polling cada 5 segundos para actualizar estados
    const intervalId = setInterval(() => {
      fetchCampaigns();
    }, 5000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetchCampaigns es estable, no necesita estar en dependencias

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/group-campaigns");

      // ✅ Validar que data sea un array
      if (!Array.isArray(data)) {
        dispatch({ type: "LOAD_CAMPAIGNS", payload: [] });
        setLoading(false);
        return;
      }

      // ✅ Filtrar campañas sin name
      const campaignsData = data.filter((c) => c && c.name);
      dispatch({ type: "LOAD_CAMPAIGNS", payload: campaignsData });
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  useEffect(() => {
    const draft = takePublicidadDraft("groups");
    if (!draft) return undefined;
    const campaignType = draft.campaignType === "RECURRENTE" ? "RECURRENTE" : "UNICA";
    setSelectedCampaign(null);
    setSelectedCampaignType(campaignType);
    setPublicidadPrefill({
      name: draft.title || "",
      message1: draft.text || "",
      tipo: campaignType === "RECURRENTE" ? "RECURRENTE" : "UNICA",
      isRecurring: campaignType === "RECURRENTE",
    });
    setPublicidadAttachment(draft.file || null);
    setCampaignModalOpen(true);
    return undefined;
  }, []);

  const handleOpenCampaignModal = (campaignType = "UNICA") => {
    setSelectedCampaign(null);
    setSelectedCampaignType(campaignType);
    setPublicidadPrefill(null);
    setPublicidadAttachment(null);
    setCampaignModalOpen(true);
  };

  const handleOpenAddMenu = (event) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleCloseAddMenu = () => {
    setAddMenuAnchorEl(null);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setSelectedCampaignType(null);
    setPublicidadPrefill(null);
    setPublicidadAttachment(null);
    setCampaignModalOpen(false);
    fetchCampaigns(); // Recargar datos después de cerrar
  };

  const handleEditCampaign = async (campaign) => {
    try {
      // ✅ CORRECCIÓN CRÍTICA: Cargar datos completos de la campaña desde el backend
      // La lista solo tiene datos básicos, necesitamos todos los campos para editar
      const { data } = await api.get(`/group-campaigns/${campaign.id}`);

      // ✅ CORRECCIÓN DE BUG: Usar originalGroupIds como fuente de verdad, no shippings
      // ✅ CRÍTICO: Para campañas recurrentes, shippings contiene TODOS los registros de todas las ejecuciones
      // Por eso debemos usar originalGroupIds como fuente de verdad, no shippings
      let groupIds = [];
      if (data.originalGroupIds) {
        try {
          groupIds = JSON.parse(data.originalGroupIds);
        } catch (e) {
          console.warn(`[GroupCampaigns] Error parseando originalGroupIds:`, e);
        }
      } else if (data.shippings && Array.isArray(data.shippings) && data.shippings.length > 0) {
        // Fallback: usar shippings pero eliminar duplicados
        groupIds = [
          ...new Set(
            data.shippings
              .map((shipping) => shipping.groupId || shipping.groupDetails?.groupId)
              .filter((id) => id)
          ),
        ];
      }

      // ✅ Preparar datos completos para el modal
      const campaignData = {
        ...data,
        groupIds, // Agregar groupIds para el selector de grupos
        whatsappId: data.whatsappId || data.whatsapp?.id,
        // ✅ Asegurar que scheduledDate conserve la hora maestra.
        // nextScheduledAt es solo la próxima ejecución operativa y no debe sobrescribirla.
        scheduledDate: data.scheduledDate || "",
        nextScheduledAt: data.nextScheduledAt || null,
        // ✅ CRÍTICO: Mapear horarios desde hourlySchedules a recurrenceHourlySchedules
        // El backend devuelve hourlySchedules como array de objetos con time, message1-5, etc.
        recurrenceHourlySchedules:
          data.hourlySchedules &&
          Array.isArray(data.hourlySchedules) &&
          data.hourlySchedules.length > 0
            ? data.hourlySchedules.map((schedule) => ({
                time: schedule.time,
                message1: schedule.message1 || "",
                message2: schedule.message2 || "",
                message3: schedule.message3 || "",
                message4: schedule.message4 || "",
                message5: schedule.message5 || "",
                mediaPath: schedule.mediaPath || null,
                mediaName: schedule.mediaName || null,
              }))
            : data.recurrenceHourlySchedules || [],
      };

      setSelectedCampaign(campaignData);
      setCampaignModalOpen(true);
    } catch (err) {
      console.error(`[GroupCampaigns] Error cargando campaña ${campaign.id}:`, err);
      toastError(err);
    }
  };

  const handleCopyCampaign = async (campaign) => {
    try {
      // Obtener datos completos de la campaña
      const { data } = await api.get(`/group-campaigns/${campaign.id}`);

      console.log("📋 [handleCopyCampaign] Datos obtenidos de la API:", data);
      console.log("📋 [handleCopyCampaign] Shippings:", data.shippings);
      console.log("📋 [handleCopyCampaign] FileListId:", data.fileListId);

      // Crear objeto con datos copiados, limpiando fecha programada
      const copiedData = {
        ...data,
        id: undefined, // No copiar ID
        name: `${data.name} - Copia`,
        scheduledDate: "",
        status: "scheduled",
        sentGroups: 0,
        failedGroups: 0,
        startDate: null,
        endDate: null,
        shippings: data.shippings || [], // ✅ Incluir shippings (grupos)
        fileListId: data.fileListId || "", // ✅ Incluir fileListId
        mediaPath: data.mediaPath || null, // ✅ Incluir mediaPath
        mediaName: data.mediaName || null, // ✅ Incluir mediaName
        // ✅ CAMPOS DE RECURRENCIA - Resetear contadores pero mantener configuración
        executionCount: 0, // Resetear contador
        nextScheduledAt: null, // Resetear próxima ejecución
        lastExecutedAt: null, // Resetear última ejecución
        completedAt: null, // Resetear fecha de completado
      };

      console.log("📋 [handleCopyCampaign] Datos copiados preparados:", copiedData);
      console.log(
        "📋 [handleCopyCampaign] Total grupos a copiar:",
        copiedData.shippings?.length || 0
      );

      // Establecer campaña para copiar
      setSelectedCampaign(copiedData);
      setCampaignModalOpen(true);

      toast.success("Campaña copiada. Revise y ajuste el nombre antes de crear.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/group-campaigns/${campaignId}`);
      dispatch({ type: "DELETE_CAMPAIGN", payload: campaignId });
      toast.success("Campaña eliminada exitosamente");
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
  };

  const handlePauseCampaign = (campaign) => {
    setCampaignToPause(campaign);
    setPauseModalOpen(true);
  };

  const confirmPauseCampaign = async () => {
    if (!campaignToPause) return;

    try {
      await api.post(`/group-campaigns/${campaignToPause.id}/cancel`);
      toast.success("Campaña pausada exitosamente");
      fetchCampaigns();
      setPauseModalOpen(false);
      setCampaignToPause(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleStopCampaign = (campaign) => {
    setCampaignToStop(campaign);
    setStopModalOpen(true);
  };

  const confirmStopCampaign = async () => {
    if (!campaignToStop) return;

    try {
      await api.post(`/group-campaigns/${campaignToStop.id}/stop`);
      toast.success("Campaña parada definitivamente");
      fetchCampaigns();
      setStopModalOpen(false);
      setCampaignToStop(null);
    } catch (err) {
      toastError(err);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/group-campaigns/${campaign.id}/restart`);
      toast.success("Campaña reanudada exitosamente");
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      scheduled: { label: "Programada", className: classes.statusScheduled },
      processing: { label: "En Proceso", className: classes.statusProcessing },
      completed: { label: "Completada", className: classes.statusCompleted },
      failed: { label: "Fallida", className: classes.statusFailed },
      cancelled: { label: "Cancelada", className: classes.statusCancelled },
    };

    const statusInfo = statusMap[status] || statusMap.scheduled;

    return (
      <Chip
        label={statusInfo.label}
        size="small"
        className={`${classes.statusChip} ${statusInfo.className}`}
      />
    );
  };

  const filteredCampaigns = campaigns.filter((campaign) => {
    // ✅ Validar que campaign y campaign.name existan antes de filtrar
    if (!campaign) {
      return false;
    }
    // Si no hay searchParam, mostrar todas las campañas (incluso sin name)
    if (searchParam && searchParam.trim() !== "") {
      if (!campaign.name) {
        return false;
      }
      if (!campaign.name.toLowerCase().includes(searchParam)) {
        return false;
      }
    }
    if (typeFilter === "unica" && campaign.tipo === "GRUPO_RECURRENTE") {
      return false;
    }
    if (typeFilter === "recurrente" && campaign.tipo !== "GRUPO_RECURRENTE") {
      return false;
    }
    if (statusFilter && campaign.status !== statusFilter) {
      return false;
    }
    return true;
  });

  const fromBot = (campaign) => campaign?.origin === "bot";

  const openCampaignReport = (campaign) => {
    if (fromBot(campaign)) {
      setBotDetail(campaign);
      return;
    }
    setSelectedCampaignForExecutions(campaign);
    setExecutionsModalOpen(true);
  };

  return (
    <MainContainer mobileScroll>
      <WhatsAppPolicyWarning storageKey="group-campaigns" variant="groups" />
      <MainHeader>
        <Title subtitle="Envía mensajes masivos a grupos y canales de WhatsApp o Telegram y sigue el progreso de cada campaña.">
          Campañas de grupos/canales
        </Title>
        <GuideOpenButton id="groups" title="Grupos y canales" to="/group-campaigns" startId="type" />
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <CampaignsBrowseBar
          searchValue={searchParam}
          onSearch={handleSearch}
          searchPlaceholder="Buscar"
          typeValue={typeFilter}
          onTypeChange={(value) => {
            setTypeFilter(value);
            setPage(0);
          }}
          typeOptions={[
            { value: "", label: "Tipo" },
            { value: "unica", label: "Única" },
            { value: "recurrente", label: "Recurrente" },
          ]}
          statusValue={statusFilter}
          onStatusChange={(value) => {
            setStatusFilter(value);
            setPage(0);
          }}
          statusOptions={[
            { value: "", label: "Estado" },
            { value: "scheduled", label: "Programada" },
            { value: "processing", label: "En proceso" },
            { value: "completed", label: "Terminada" },
            { value: "failed", label: "Fallida" },
            { value: "cancelled", label: "Cancelada" },
          ]}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          actions={
          <>
          <ButtonGroup
            variant="contained"
            color="primary"
            style={{
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
              overflow: "visible",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Button
              onClick={() => handleOpenCampaignModal("UNICA")}
              style={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "14px",
                padding: "8px 18px",
              }}
              startIcon={<AddIcon />}
            >
              Nueva Campaña
            </Button>
            <Button
              size="small"
              onClick={handleOpenAddMenu}
              style={{
                minWidth: "36px",
                padding: "0 8px",
                borderLeft: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Menu
            anchorEl={addMenuAnchorEl}
            open={Boolean(addMenuAnchorEl)}
            onClose={handleCloseAddMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            style={{
              zIndex: 1301,
            }}
            PaperProps={{
              style: {
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                minWidth: "250px",
                marginTop: "4px",
                zIndex: 1301,
              },
            }}
            MenuListProps={{
              style: {
                padding: "4px 0",
              },
            }}
            getContentAnchorEl={null}
          >
            <MenuItem
              onClick={() => {
                handleOpenCampaignModal("UNICA");
                handleCloseAddMenu();
              }}
              style={{
                padding: "12px 20px",
                fontSize: "14px",
              }}
            >
              <Box display="flex" alignItems="center" width="100%">
                <PhoneAndroidIcon style={{ marginRight: 12, fontSize: 20 }} />
                Campaña ÚNICA
              </Box>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleOpenCampaignModal("RECURRENTE");
                handleCloseAddMenu();
              }}
              style={{
                padding: "12px 20px",
                fontSize: "14px",
              }}
            >
              <Box display="flex" alignItems="center" width="100%">
                <RepeatIcon style={{ marginRight: 12, fontSize: 20 }} />
                Campaña RECURRENTE
              </Box>
            </MenuItem>
          </Menu>
          </>
          }
        />

        {viewMode === "grid" ? (
          <Box className={classes.gridWrap}>
            {loading ? (
              <Typography variant="body2" color="textSecondary">Cargando campañas...</Typography>
            ) : filteredCampaigns.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No se encontraron campañas</Typography>
            ) : (
              filteredCampaigns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  name={campaign.name}
                  dateLabel={campaign.scheduledDate ? formatDateTime(campaign.scheduledDate) : ""}
                  connectionName={campaign.whatsapp?.name}
                  channel={getCampaignChannel(campaign)}
                  countLabel={`${campaign.sentGroups || 0} / ${campaign.totalGroups || 0}`}
                  statusChip={getStatusChip(campaign.status)}
                  onOpen={() => openCampaignReport(campaign)}
                  actions={
                    <>
                      {fromBot(campaign) ? null : (
                      <IconButton size="small" onClick={() => handleEditCampaign(campaign)} className={classes.actionIcon}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      )}
                      {fromBot(campaign) ? null : (
                      <IconButton size="small" onClick={() => handleCopyCampaign(campaign)} className={classes.actionIcon} aria-label="Duplicar campaña">
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeletingCampaign(campaign.id);
                          setConfirmModalOpen(true);
                        }}
                        className={classes.deleteIcon}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  }
                />
              ))
            )}
          </Box>
        ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Nombre</TableCell>
              <TableCell className={classes.tableHeader}>Adjunto</TableCell>
              <TableCell className={classes.tableHeader}>Estado</TableCell>
              <TableCell className={classes.tableHeader}>Tipo</TableCell>
              <TableCell className={classes.tableHeader}>Grupos</TableCell>
              <TableCell className={classes.tableHeader}>Ejecuciones</TableCell>
              <TableCell className={classes.tableHeader}>Próxima Ejecución</TableCell>
              <TableCell className={classes.tableHeader}>Conexión</TableCell>
              <TableCell className={classes.tableHeader}>Programada</TableCell>
              <TableCell className={classes.tableHeader}>Completada</TableCell>
              <TableCell className={classes.tableHeader}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              // ✅ PAGINACIÓN: Calcular campañas paginadas (igual que Campaigns)
              const paginatedCampaigns = filteredCampaigns.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              );

              if (loading) {
                return <TableRowSkeleton columns={11} />;
              }

              if (paginatedCampaigns.length > 0) {
                return paginatedCampaigns.map((campaign) => (
                  <TableRow
                    key={campaign.id}
                    className={classes.tableRow}
                    onClick={() => openCampaignReport(campaign)}
                  >
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell>
                      {campaign.mediaPath || campaign.mediaName ? (
                        <AttachmentPreview
                          mediaPath={campaign.mediaPath}
                          mediaName={campaign.mediaName}
                          size="compact"
                          showInfo={false}
                        />
                      ) : campaign.fileListId ? (
                        "Lista"
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{getStatusChip(campaign.status)}</TableCell>
                    <TableCell>
                      {fromBot(campaign) ? (
                        <Chip label="Bot de Telegram" color="primary" size="small" />
                      ) : campaign.tipo === "GRUPO_RECURRENTE" ? (
                        <Chip
                          icon={<RepeatIcon />}
                          label="Recurrente"
                          color="primary"
                          size="small"
                          style={{ fontWeight: "bold" }}
                        />
                      ) : (
                        <Chip label="Única" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {/* ✅ MEJORA UX: Mostrar contador X/Y con chips (igual que Campaigns) */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {/* Contador X/Y - Siempre visible si hay datos, o mostrar 0/0 si no hay */}
                        {(() => {
                          const sentCount = campaign.sentGroups || 0;
                          const totalCount = campaign.totalGroups || 0;

                          return (
                            <Chip
                              label={`${sentCount}/${totalCount}`}
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                openCampaignReport(campaign);
                              }}
                              style={{
                                backgroundColor:
                                  sentCount === totalCount && totalCount > 0
                                    ? "#4caf50"
                                    : sentCount > 0
                                      ? "#ff9800"
                                      : "#e3f2fd",
                                color:
                                  sentCount === totalCount && totalCount > 0
                                    ? "white"
                                    : sentCount > 0
                                      ? "white"
                                      : "#1976d2",
                                fontWeight: "bold",
                                minWidth: "50px",
                                cursor: "pointer",
                              }}
                              title="Click para ver detalle de envíos"
                            />
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell>
                      {/* ✅ REGLA DE NEGOCIO: Solo usar campaign.tipo - No puede haber UNICA con recurrencia */}
                      {campaign.tipo === "GRUPO_RECURRENTE" &&
                      campaign.executionCount !== undefined &&
                      campaign.executionCount !== null ? (
                        <span style={{ color: "#1976d2", fontWeight: "bold" }}>
                          {campaign.executionCount}
                        </span>
                      ) : (
                        <span style={{ color: "#9e9e9e" }}>-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* ✅ REGLA DE NEGOCIO: Solo usar campaign.tipo - No puede haber UNICA con recurrencia */}
                      {campaign.tipo === "GRUPO_RECURRENTE" && campaign.nextScheduledAt ? (
                        <span style={{ color: "#1976d2", fontWeight: "bold" }}>
                          {formatDateTime(campaign.nextScheduledAt)}
                        </span>
                      ) : (
                        <span style={{ color: "#9e9e9e" }}>-</span>
                      )}
                    </TableCell>
                    <TableCell><ChannelConnectionLabel channel={getCampaignChannel(campaign)} name={campaign.whatsapp?.name} /></TableCell>
                    <TableCell>
                      {campaign.scheduledDate ? formatDateTime(campaign.scheduledDate) : "-"}
                    </TableCell>
                    <TableCell align="center">
                      {/* ✅ CORRECCIÓN: Mostrar solo completedAt (fecha de completado), no lastExecutedAt */}
                      {campaign.completedAt ? formatDateTime(campaign.completedAt) : "-"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {campaign.status === "processing" && !fromBot(campaign) && (
                        <IconButton
                          onClick={() => handlePauseCampaign(campaign)}
                          title="Pausar Campaña"
                          size="small"
                          className={classes.pauseIcon}
                        >
                          <PauseCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                      {/* ✅ Botón Parar - para campañas programadas o en proceso */}
                      {(campaign.status === "scheduled" || campaign.status === "processing") && (
                        <IconButton
                          onClick={() => handleStopCampaign(campaign)}
                          title="Parar Campaña Definitivamente"
                          size="small"
                          className={classes.stopIcon}
                        >
                          <StopIcon fontSize="small" />
                        </IconButton>
                      )}
                      {/* ✅ Botón Reanudar - para campañas canceladas con nextScheduledAt */}
                      {campaign.status === "cancelled" && campaign.nextScheduledAt && !fromBot(campaign) && (
                        <IconButton
                          onClick={() => restartCampaign(campaign)}
                          title="Reanudar Campaña"
                          size="small"
                          className={classes.playIcon}
                        >
                          <PlayCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                      {/* Duplicar: disponible en cualquier estado (antes solo completadas) */}
                      {!fromBot(campaign) && (
                        <IconButton
                          size="small"
                          onClick={() => handleCopyCampaign(campaign)}
                          className={classes.actionIcon}
                          title="Duplicar campaña"
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      )}
                      {fromBot(campaign) ? null : (
                      <IconButton
                        size="small"
                        onClick={() => handleEditCampaign(campaign)}
                        className={classes.actionIcon}
                        title="Editar campaña"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeletingCampaign(campaign.id);
                          setConfirmModalOpen(true);
                        }}
                        className={classes.deleteIcon}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ));
              }

              return (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    "No se encontraron campañas"
                  </TableCell>
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
        )}

        {/* ✅ PAGINACIÓN: Solo aparece cuando hay resultados (igual que Campaigns) */}
        {!loading && filteredCampaigns.length > 0 && (
          <TablePagination
            component="div"
            count={filteredCampaigns.length}
            page={page}
            onPageChange={(event, newPage) => {
              setPage(newPage);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 13, 20, 50, 100]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            style={{
              backgroundColor: "transparent",
              color: "inherit",
            }}
          />
        )}
      </Paper>

      <ConfirmationModal
        title="Eliminar Campaña"
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingCampaign(null);
        }}
        onConfirm={() => {
          if (deletingCampaign) {
            handleDeleteCampaign(deletingCampaign);
          }
          setConfirmModalOpen(false);
        }}
      >
        ¿Está seguro que desea eliminar esta campaña?
      </ConfirmationModal>

      {/* Modal de confirmación para Pausar */}
      <Dialog
        open={pauseModalOpen}
        onClose={() => {
          setPauseModalOpen(false);
          setCampaignToPause(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pausar Campaña</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            ¿Está seguro de pausar esta campaña? Los mensajes ya enviados no se revierten. Podrá
            reanudarla después.
          </Typography>
          {campaignToPause && (
            <Typography variant="body2" style={{ marginTop: 16, fontWeight: "bold" }}>
              Campaña: {campaignToPause.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPauseModalOpen(false);
              setCampaignToPause(null);
            }}
            color="default"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button onClick={confirmPauseCampaign} color="primary" variant="contained">
            Pausar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación para Parar */}
      <Dialog
        open={stopModalOpen}
        onClose={() => {
          setStopModalOpen(false);
          setCampaignToStop(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ color: "#d32f2f" }}>
          <Box display="flex" alignItems="center">
            <WarningIcon style={{ marginRight: 8 }} />
            Parar Campaña Definitivamente
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body1"
            style={{ fontWeight: "bold", color: "#d32f2f", marginBottom: 16 }}
          >
            ACCIÓN IRREVERSIBLE: ¿Está seguro de PARAR DEFINITIVAMENTE esta campaña?
          </Typography>
          <Typography variant="body2">
            Esta acción NO se puede deshacer. Los mensajes ya enviados se mantendrán, pero NO podrá
            reanudar la campaña.
          </Typography>
          {campaignToStop && (
            <Typography variant="body2" style={{ marginTop: 16, fontWeight: "bold" }}>
              Campaña: {campaignToStop.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStopModalOpen(false);
              setCampaignToStop(null);
            }}
            color="default"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button onClick={confirmStopCampaign} color="secondary" variant="contained">
            Parar Definitivamente
          </Button>
        </DialogActions>
      </Dialog>

      <GroupCampaignModal
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        campaignId={selectedCampaign?.id}
        initialValues={selectedCampaign || publicidadPrefill}
        initialAttachment={!selectedCampaign ? publicidadAttachment : null}
        campaignType={
          selectedCampaignType ||
          (selectedCampaign &&
            (selectedCampaign.tipo || (selectedCampaign.isRecurring ? "RECURRENTE" : "UNICA"))) ||
          "UNICA"
        }
        onSave={() => {
          fetchCampaigns();
        }}
      />

      <Dialog open={Boolean(botDetail)} onClose={() => setBotDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Campaña del bot de Telegram</DialogTitle>
        <DialogContent>
          {botDetail ? (
            <Box>
              <Typography variant="h6">{botDetail.name}</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {botDetail.audienceLabel || "Audiencia del bot"}
              </Typography>
              <Box display="flex" alignItems="center" style={{ marginTop: 8, gap: 8 }}>
                <Typography variant="body2">Estado</Typography>
                {getStatusChip(botDetail.status)}
              </Box>
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Programada: {botDetail.scheduledDate ? formatDateTime(botDetail.scheduledDate) : "—"}
              </Typography>
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Enviados {botDetail.sentGroups || 0} de {botDetail.totalGroups || 0}. Fallidos {botDetail.failedGroups || 0}.
              </Typography>
              {botDetail.messagePreview ? (
                <Typography variant="body2" style={{ marginTop: 12 }}>{botDetail.messagePreview}</Typography>
              ) : null}
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                Esta campaña se creó en el bot. Pararla o eliminarla aquí también la quita de Mis campañas.
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBotDetail(null)} color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Ejecuciones */}
      {selectedCampaignForExecutions && (
        <GroupCampaignExecutionsModal
          open={executionsModalOpen}
          onClose={() => {
            setExecutionsModalOpen(false);
            setSelectedCampaignForExecutions(null);
          }}
          campaignId={selectedCampaignForExecutions.id}
          campaignName={selectedCampaignForExecutions.name}
        />
      )}
    </MainContainer>
  );
};

export default GroupCampaigns;
